/**
 * Onglet Bibliothèque — les trois objets du référentiel que le §8.2 des specs
 * range ensemble : modèles de journée, aliments, recettes.
 *
 * 🔴 **Trois sous-onglets, un par objet** — demandé après usage. Les trois
 * sections vivaient auparavant l'une sous l'autre sur une seule page : chercher
 * une recette demandait de faire défiler tout le catalogue d'aliments, et
 * l'écran n'avait pas de fin. Ce n'est pas un simple découpage visuel :
 *
 *   - **la section est dans l'URL**, donc la mémoire d'onglet
 *     (`lib/tabMemory.ts`) sait y revenir, et le retour par glissement natif la
 *     traverse sans qu'on écrive quoi que ce soit (§6.1) ;
 *   - chaque section ne monte que lorsqu'elle est regardée, donc ses requêtes
 *     ne partent plus toutes les trois à l'ouverture de l'onglet.
 *
 * ⚠️ **L'ordre des sous-onglets est celui qui a été demandé** — modèles,
 * aliments, recettes — et non celui du §8.2, qui cite « Aliments, recettes,
 * modèles de journée ». Le §8.2 énumère un contenu, il ne prescrit pas un
 * ordre ; celui-ci correspond à ce qui a été demandé à l'usage.
 *
 * ⚠️ La **section par défaut** est la première, et c'est là que mène un second
 * appui sur l'onglet. Toute autre règle serait une surprise : on ne s'attend
 * pas à retomber au milieu d'une rangée d'onglets.
 */

import { useParams } from 'react-router';

import { ScreenTitle } from '../../components/ui/ScreenHeader';
import { SectionTabs, type SectionTab } from '../../components/ui/SectionTabs';
import { FoodsSection } from './FoodsSection';
import { RecipesSection } from './RecipesSection';
import { TemplatesSection } from './TemplatesSection';

/**
 * 🔴 **Trois sous-onglets — les exercices sont partis dans « Muscu »**
 * *(28/08/2026, après usage réel sur l'appareil)*.
 *
 * L'étape 4 les avait rangés ici, conformément au §4.6 de la V2 et au §8.2 des
 * specs (« Bibliothèque : + Exercices, routines »). Le raisonnement se tenait —
 * un exercice *est* un objet de référentiel, comme un aliment ou une recette —
 * mais il ratait ce qui décide de l'emplacement, et que le §4.6 énonce
 * lui-même : « l'objet va là où se trouve le geste qui l'emploie ». On ouvre un
 * exercice en composant une routine ou en salle, jamais en même temps qu'on
 * corrige les macros d'un yaourt. **La Bibliothèque redevient l'onglet de la
 * nutrition.**
 *
 * ⚠️ **Le correctif `flex: 0 0 auto` de `SectionTabs` reste, et doit rester.**
 * C'est le quatrième libellé qui avait révélé le défaut (V2 §2.6) et il n'y en
 * a plus que trois — mais le défaut, lui, n'était pas lié au nombre : la rangée
 * ne glissait *jamais*, elle comprimait ses libellés en silence. Le retirer
 * remettrait une bombe à retardement pour le prochain sous-onglet.
 */
const TABS: readonly SectionTab[] = [
  // `null` = la section par défaut, servie par `/bibliotheque` tout court.
  { section: null, label: 'Modèles' },
  { section: 'aliments', label: 'Aliments' },
  { section: 'recettes', label: 'Recettes' },
];

export function LibraryScreen() {
  const { section } = useParams<{ section?: string }>();

  /*
   * ⚠️ Une section inconnue retombe sur celle par défaut plutôt que d'afficher
   * une page vide : `/bibliotheque/nimportequoi` reste une adresse qui montre
   * quelque chose. Le sous-onglet actif suit — d'où la normalisation ici et
   * pas seulement dans le rendu.
   */
  const courante =
    section === 'aliments' || section === 'recettes' ? section : null;

  return (
    <section className="n-page">
      <ScreenTitle>Bibliothèque</ScreenTitle>

      <SectionTabs
        base="/bibliotheque"
        tabs={TABS}
        current={courante}
        ariaLabel="Sections de la bibliothèque"
      />

      {courante === null && <TemplatesSection />}
      {courante === 'aliments' && <FoodsSection />}
      {courante === 'recettes' && <RecipesSection />}
    </section>
  );
}
